/**
 * Active-clinic selection (frontend side of the backend's x-clinic-id rule).
 * The header is a selector among the caller's own memberships — never a
 * tenant-authority claim; the backend validates every request against live
 * memberships. Selection persists per uid (`zc.clinic.<uid>`) so a returning
 * user lands back in their clinic and one person's memory never becomes
 * another's; hostname plays no role by design (ADR-013).
 *
 * ZOY-610 adds the three things a multi-tab, multi-clinic session needs:
 *  - a "context lost" signal the HTTP layer raises on 403 CLINIC_CONTEXT_INVALID
 *    / CLINIC_INACTIVE from ANY clinic request, so the app re-resolves instead
 *    of showing a raw error;
 *  - a FREEZE: once another tab switched clinic, this tab refuses to issue
 *    clinic requests until the person reloads (or signs out) — it never keeps
 *    acting in a clinic the user left;
 *  - a storage subscription so a switch or sign-out elsewhere is noticed here.
 */

let selectedClinicId: string | null = null;

export function getSelectedClinicId(): string | null {
  return selectedClinicId;
}

export function setSelectedClinicId(id: string | null): void {
  selectedClinicId = id;
}

const storageKey = (uid: string) => `zc.clinic.${uid}`;

export function readStoredClinic(uid: string): string | null {
  try {
    return localStorage.getItem(storageKey(uid));
  } catch {
    return null;
  }
}

export function storeClinic(uid: string, clinicId: string): void {
  try {
    localStorage.setItem(storageKey(uid), clinicId);
  } catch {
    /* ignore */
  }
}

export function clearStoredClinic(uid: string): void {
  try {
    localStorage.removeItem(storageKey(uid));
  } catch {
    /* ignore */
  }
}

/* ------------------------- context lost (server said so) ------------------------- */

export type ClinicContextLostCode = 'CLINIC_CONTEXT_INVALID' | 'CLINIC_INACTIVE';
export const CLINIC_CONTEXT_LOST_CODES: readonly string[] = [
  'CLINIC_CONTEXT_INVALID',
  'CLINIC_INACTIVE',
];

type LostListener = (code: ClinicContextLostCode) => void;
const lostListeners = new Set<LostListener>();

export function onClinicContextLost(listener: LostListener): () => void {
  lostListeners.add(listener);
  return () => lostListeners.delete(listener);
}

/** Raised by the HTTP layer; the authz provider re-resolves. */
export function notifyClinicContextLost(code: ClinicContextLostCode): void {
  for (const l of lostListeners) l(code);
}

/* ------------------------------ frozen by another tab ---------------------------- */

export interface FrozenContext {
  /** What the other tab selected (null = it cleared the selection). */
  clinicId: string | null;
}

let frozen: FrozenContext | null = null;
type FrozenListener = (f: FrozenContext | null) => void;
const frozenListeners = new Set<FrozenListener>();

export function freezeClinicContext(info: FrozenContext): void {
  frozen = info;
  for (const l of frozenListeners) l(frozen);
}

/** Only a reload (fresh module) or sign-out ends a freeze. */
export function unfreezeClinicContext(): void {
  frozen = null;
  for (const l of frozenListeners) l(null);
}

export function frozenClinicContext(): FrozenContext | null {
  return frozen;
}

export function onClinicContextFrozen(listener: FrozenListener): () => void {
  frozenListeners.add(listener);
  return () => frozenListeners.delete(listener);
}

/* ------------------------------- cross-tab watch --------------------------------- */

/**
 * Fires when ANOTHER tab writes or removes this uid's stored clinic (the
 * `storage` event never fires in the tab that made the change).
 */
export function subscribeStoredClinic(
  uid: string,
  listener: (clinicId: string | null) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const key = storageKey(uid);
  const handler = (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;
    if (e.key === key || e.key === null) listener(e.key === null ? null : e.newValue);
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
